import { CSSProperties } from 'react';
import ColorResource from '../../../services/resources/ColorResource';
import { ITheme } from '../../../providers/ThemeProvider';

/**
 * The look of the Stripe Payment Element as this app configures it — `theme: 'stripe'` (or
 * `'night'` in dark mode) with `colorPrimary` and `colorText` both set to the brand text colour,
 * nothing else (see PaymentCoordinator). A Spreedly company's card form sits in the very same
 * payment-method row, so it copies those neutrals rather than the storefront's own tokens, which
 * the Payment Element never sees either.
 *
 * Values are Stripe's default theme CSS: `#e6e6e6` input border, 5px radius, two-layer soft
 * shadow, 16px inputs, 12px padding, focus = colorPrimary at 50% on the border + a 3px ring at 25%,
 * invalid = colorDanger border + 1px ring.
 */
interface StripeNeutrals {
  background: string;
  border: string;
  placeholder: string;
  danger: string;
}

const STRIPE_THEME: StripeNeutrals = {
  background: '#ffffff',
  border: '#e6e6e6',
  placeholder: '#77787f',
  danger: '#df1b41',
};

const NIGHT_THEME: StripeNeutrals = {
  background: '#30313d',
  border: '#424353',
  placeholder: '#8b8d98',
  danger: '#fe87a1',
};

const RESTING_SHADOW = '0 1px 1px rgba(0, 0, 0, 0.03), 0 3px 6px rgba(18, 42, 66, 0.02)';
const FOCUS_RING_WIDTH_PX = 3;
const INVALID_RING_WIDTH_PX = 1;

export const FIELD_HEIGHT_PX = 44;
export const FIELD_FONT_SIZE = '16px';
export const FIELD_PADDING_X = '12px';
export const FIELD_RADIUS = '5px';

export interface FieldAppearance extends StripeNeutrals {
  /** `colorText` and `colorPrimary` — the brand text colour, exactly as the Payment Element gets it. */
  text: string;
  primary: string;
}

export function fieldAppearance(theme: ITheme): FieldAppearance {
  const text = theme.color.brand.text.primary;

  return {
    ...(theme.isDarkMode ? NIGHT_THEME : STRIPE_THEME),
    text,
    primary: text,
  };
}

// Comma syntax on purpose: every engine (jsdom included) parses it, unlike `rgb(r g b / a)`.
const withAlpha = (color: string, alpha: number) =>
  `rgba(${ColorResource.getRgbValue(color).split(' ').join(', ')}, ${alpha})`;

export interface FieldVisualState {
  focused: boolean;
  invalid: boolean;
}

/**
 * Inline styles for one field box (a plain input or an iFrame wrapper). Inline because the focus
 * and invalid colours derive from the runtime brand colour with alpha, which Tailwind classes
 * cannot express, and because the two iFrame boxes have no `:focus` of their own — Spreedly
 * reports focus through events.
 *
 * Invalid and focused compose the way Stripe's `.Input--invalid:focus` does: the border keeps
 * colorDanger so the problem stays visible while the customer is back in the field, and the focus
 * ring is layered on top of it.
 */
export function fieldStyle(appearance: FieldAppearance, state: FieldVisualState): CSSProperties {
  const borderColor = state.invalid
    ? appearance.danger
    : state.focused
      ? withAlpha(appearance.primary, 0.5)
      : appearance.border;

  const ring = state.focused
    ? `0 0 0 ${FOCUS_RING_WIDTH_PX}px ${withAlpha(appearance.primary, 0.25)}, `
    : state.invalid
      ? `0 0 0 ${INVALID_RING_WIDTH_PX}px ${appearance.danger}, `
      : '';

  return {
    backgroundColor: appearance.background,
    color: appearance.text,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor,
    borderRadius: FIELD_RADIUS,
    boxShadow: `${ring}${RESTING_SHADOW}`,
    outline: 'none',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  };
}

/** The box's 1px border on each side is ours; the hosted input fills what is left. */
export const IFRAME_INPUT_HEIGHT_PX = FIELD_HEIGHT_PX - 2;

/**
 * What goes into Spreedly's own documents, where only a CSS string can reach. The height is
 * explicit: inside the iFrame `height: 100%` has nothing to resolve against, so the input would
 * keep its intrinsic ~20px and sit at the top of our 44px box.
 */
export function iframeInputCss(appearance: FieldAppearance, fontFamily: string): string {
  return (
    `display: block; width: 100%; height: ${IFRAME_INPUT_HEIGHT_PX}px; line-height: ${IFRAME_INPUT_HEIGHT_PX}px;` +
    ` margin: 0; box-sizing: border-box; padding: 0 ${FIELD_PADDING_X}; border: none; outline: none;` +
    ` background: transparent; font-size: ${FIELD_FONT_SIZE}; font-family: ${fontFamily}; color: ${appearance.text};`
  );
}

export function iframePlaceholderCss(appearance: FieldAppearance, fontFamily: string): string {
  return `color: ${appearance.placeholder}; font-family: ${fontFamily};`;
}
